import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Expand } from "lucide-react";
import { Container, CodeTag } from "@/components/Section";
import Seo from "@/components/Seo";
import { Lightbox } from "@/components/Lightbox";
import { fetchProject } from "@/lib/api";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetchProject(slug).then(setProject).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-display text-4xl uppercase">Project not found</h1>
        <Link to="/projects" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] link-underline">
          <ArrowLeft className="w-4 h-4" /> Back to projects
        </Link>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container className="py-32 font-mono text-sm uppercase tracking-[0.25em]">Loading…</Container>
    );
  }

  const meta = [
    ["Client", project.client],
    ["Location", project.location],
    ["Year", project.year],
    ["Contract Value", project.value],
    ["Duration", project.duration],
    ["Category", project.category],
  ];

  const lightboxImages = [
    { src: project.cover_image, alt: project.title },
    ...(project.gallery || []).map((g, i) => ({ src: g, alt: `${project.title} — field photo ${i + 1}` })),
  ];

  return (
    <div data-testid="project-detail-page">
      <Seo
        title={`${project.title} | APRASIM Projects`}
        description={project.summary}
        path={`/projects/${project.slug}`}
      />
      {/* HEADER */}
      <section className="border-b-2 border-black">
        <Container className="py-12">
          <Link to="/projects" data-testid="back-to-projects" className="font-mono text-[11px] uppercase tracking-[0.25em] inline-flex items-center gap-2 link-underline">
            <ArrowLeft className="w-4 h-4" /> All Projects
          </Link>
          <div className="mt-8 grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-8">
              <CodeTag>{project.code}</CodeTag>
              <h1 className="mt-5 font-display text-4xl sm:text-6xl uppercase tracking-tighter leading-[0.92]">
                {project.title}
              </h1>
              <p className="mt-6 text-lg text-neutral-700 leading-relaxed max-w-2xl">{project.summary}</p>
            </div>
            <div className="col-span-12 lg:col-span-4 brut-border p-6 bg-[#E5E5DF]">
              <div className="label-mono text-[#FF4F00]">Fact Sheet</div>
              <dl className="mt-4 grid grid-cols-2 gap-y-3 text-sm">
                {meta.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-600">{k}</dt>
                    <dd className="font-mono text-sm">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </Container>
      </section>

      {/* COVER IMAGE */}
      <section className="border-b-2 border-black">
        <button
          type="button"
          data-testid="cover-image-open"
          onClick={() => setLightboxIndex(0)}
          className="group relative block w-full bg-black overflow-hidden cursor-zoom-in"
          aria-label="View cover photo full screen"
        >
          <img src={project.cover_image} alt={project.title} className="w-full max-h-[640px] object-cover img-gray" />
          <span className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/70 text-[#F0F0EC] font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Expand className="w-4 h-4" /> View Full Screen
          </span>
        </button>
      </section>

      {/* BODY */}
      <section className="border-b-2 border-black">
        <Container className="py-16 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <div className="label-mono text-[#FF4F00]">Scope & Delivery</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl uppercase tracking-tight leading-[0.95]">
              How it got built
            </h2>
            <p className="mt-6 text-neutral-700 leading-relaxed text-lg">{project.description}</p>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="brut-border p-8 bg-[#F0F0EC]">
              <div className="label-mono">Key Scope</div>
              <ul className="mt-5 space-y-3">
                {project.scope.map((s) => (
                  <li key={s} className="flex items-start gap-3 font-mono text-sm">
                    <span className="mt-1 w-2 h-2 bg-[#FF4F00] flex-shrink-0" />
                    <span className="uppercase tracking-wider">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* GALLERY */}
      {project.gallery && project.gallery.length > 0 && (
        <section className="border-b-2 border-black">
          <Container className="py-16">
            <div className="label-mono text-[#FF4F00] mb-6">Field Log</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.gallery.map((g, i) => (
                <button
                  type="button"
                  key={i}
                  data-testid={`gallery-photo-${i}`}
                  onClick={() => setLightboxIndex(i + 1)}
                  className="group relative brut-border overflow-hidden bg-black cursor-zoom-in text-left"
                  aria-label={`View photo ${i + 1} full screen`}
                >
                  <img src={g} alt={`gallery ${i + 1}`} className="w-full h-80 object-cover img-gray img-zoom" />
                  <span className="absolute bottom-3 right-3 flex items-center gap-2 bg-black/70 text-[#F0F0EC] font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Expand className="w-4 h-4" /> Expand
                  </span>
                </button>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* CTA */}
      <section className="border-b-2 border-black bg-black text-[#F0F0EC]">
        <Container className="py-16 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 lg:col-span-8">
            <div className="label-mono text-[#FF4F00]">Similar Scope?</div>
            <h2 className="mt-3 font-display text-3xl sm:text-5xl uppercase tracking-tight leading-[0.95]">
              Send us your package.<br />We&apos;ll price it sharp.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="project-cta-bid"
              className="inline-flex items-center gap-3 bg-[#FF4F00] text-black px-7 py-5 font-mono text-xs uppercase tracking-[0.25em] hover:-translate-y-1 transition-transform brut-shadow"
              style={{ boxShadow: "8px 8px 0 0 #F0F0EC" }}
            >
              Request a Bid <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </Container>
      </section>

      <Lightbox
        images={lightboxImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
}
